import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import NProgress from 'nprogress'
import { useTranslation } from 'react-i18next'

import { Modal, ModalProps } from 'components/Modal'
import { Input } from 'components/Input'
import { Textarea } from 'components/Textarea'
import { PATH_FLOW } from 'pages/FlowPage'
import { ApiError, AppStartTypeValue, FlowDto } from 'api/models'
import { queryKeys } from 'hooks/useApiQuery'
import { AxiosError } from 'axios'
import { useApi } from 'contexts/di-context'
import { CheckboxV2 } from 'components/Checkbox'
import { FEATURE_FLAGS, useFeatureFlag } from 'utils/feature-flags'

interface FlowFormModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  flowModel?: Pick<FlowDto, 'name' | 'description' | 'appStartType' | 'hidden' | 'automationConfig'>
  isEdit?: boolean
  onlyDomainEdit?: boolean
  onDeleteFlowClick?: () => void
}

const NAME_VALUE_MIN_LENGTH = 0
const NAME_VALUE_MAX_LENGTH = 35
const DESCRIPTION_VALUE_MAX_LENGTH = 240

export const FlowFormModal = ({
  isOpen,
  onClose,
  isEdit,
  onDeleteFlowClick,
  flowModel,
}: FlowFormModalProps) => {
  const { t } = useTranslation()
  const api = useApi()
  const { projectUrlName, flowProjectLocalId } = useParams() as {
    projectUrlName: string
    flowProjectLocalId: string
  }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isHiddenFlowEnabled = useFeatureFlag(FEATURE_FLAGS.HIDDEN_FLOW) ?? false
  const isRegressionAnalysisEnabled = useFeatureFlag(FEATURE_FLAGS.REGRESSION_ANALYSIS) ?? false

  const getValues = useCallback(() => {
    return {
      name: flowModel?.name || '',
      description: flowModel?.description || '',
    }
  }, [flowModel])

  const [nameValue, setNameValue] = useState(getValues().name)
  const [descriptionValue, setDescriptionValue] = useState(getValues().description)
  const [isFlowHidden, setIsFlowHidden] = useState(flowModel?.hidden ?? false)
  const [isFlowSupportsRa, setIsFlowSupportsRa] = useState(
    flowModel?.automationConfig?.supportsRa ?? false,
  )

  useEffect(() => {
    setNameValue(getValues().name)
    setDescriptionValue(getValues().description)
  }, [getValues, isOpen])

  const handleNameValueChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setNameValue(event.target.value)

  const handleDescriptionValueChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setDescriptionValue(event.target.value)

  const handleHiddenChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFlowHidden(event.target.checked)
  }, [])

  const handleSupportsRaChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFlowSupportsRa(event.target.checked)
  }, [])

  const postFlowMutation = useMutation(
    () => {
      NProgress.start()
      return api.postFlow(
        { projectUrlName },
        {
          name: nameValue,
          description: descriptionValue,
          hidden: isFlowHidden,
          supportsRa: isFlowSupportsRa,
        },
      )
    },
    {
      onSuccess: (data) => {
        NProgress.done()
        onClose()
        queryClient.setQueryData<FlowDto[] | undefined>(
          queryKeys.flows({ projectUrlName }),
          (existingFlows = []) => {
            return [...existingFlows, data]
          },
        )

        navigate(
          generatePath(PATH_FLOW, {
            projectUrlName,
            flowProjectLocalId: String(data.projectLocalId),
          }),
        )
      },
      onError: (err: AxiosError<ApiError>) => {
        NProgress.done()
        toast.error(err.response?.data.message ?? t('errorMessage'))
      },
    },
  )

  const putFlowMutation = useMutation(
    () => {
      NProgress.start()
      return api.putFlow(
        { projectUrlName, flowProjectLocalId },
        {
          name: nameValue,
          description: descriptionValue,
          appStartType: flowModel?.appStartType as AppStartTypeValue,
        },
      )
    },
    {
      onSuccess: (data) => {
        NProgress.done()
        onClose()
        queryClient.setQueryData<FlowDto | undefined>(
          queryKeys.flow({ projectUrlName, flowProjectLocalId }),
          (oldData) => {
            if (oldData) {
              return { ...oldData, ...data }
            }
          },
        )
      },
      onError: (err: AxiosError<ApiError>) => {
        NProgress.done()
        toast.error(err.response?.data.message ?? t('errorMessage'))
      },
    },
  )

  const isValid = useMemo(() => {
    return (
      nameValue.length > NAME_VALUE_MIN_LENGTH &&
      nameValue.length <= NAME_VALUE_MAX_LENGTH &&
      descriptionValue.length <= DESCRIPTION_VALUE_MAX_LENGTH
    )
  }, [nameValue, descriptionValue])

  return (
    <Modal
      title={isEdit ? t('flows.flowFormModal.editTitle') : t('flows.flowFormModal.addTitle')}
      isOpen={isOpen}
      onClose={onClose}
      additionalButton={
        isEdit
          ? { children: t('flows.flowFormModal.deleteButton'), onClick: onDeleteFlowClick }
          : {}
      }
      actionButton={{
        children: isEdit ? t('flows.flowFormModal.editButton') : t('flows.flowFormModal.addButton'),
        onClick: isEdit ? putFlowMutation.mutate : postFlowMutation.mutate,
        disabled: (isEdit ? putFlowMutation.isLoading : postFlowMutation.isLoading) || !isValid,
      }}
    >
      <div className="mt-[16px] w-full">
        <Input
          value={nameValue}
          onChange={handleNameValueChange}
          placeholder={t('flows.flowFormModal.flowName')}
          maxLength={NAME_VALUE_MAX_LENGTH}
          inModal
          containerClassName="w-[100%]"
        />
        <Textarea
          value={descriptionValue}
          onChange={handleDescriptionValueChange}
          placeholder={t('flows.flowFormModal.description')}
          maxLength={DESCRIPTION_VALUE_MAX_LENGTH}
          inModal
          containerClassName="w-[100%]"
        />
        {isHiddenFlowEnabled && (
          <CheckboxV2
            checked={isFlowHidden}
            onChange={handleHiddenChange}
            disabled={Boolean(flowModel)}
            className="w-fit"
          >
            {t('flows.flowFormModal.hidden')}
          </CheckboxV2>
        )}
        {isRegressionAnalysisEnabled && (
          <CheckboxV2
            checked={isFlowSupportsRa}
            onChange={handleSupportsRaChange}
            disabled={Boolean(flowModel)}
            className="w-fit"
          >
            {t('flows.flowFormModal.supportsRa')}
          </CheckboxV2>
        )}
      </div>
    </Modal>
  )
}
